import { FC, ReactNode } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FieldError } from "react-hook-form";
import {
  IconButton,
  InputProps as ChakraInputProps,
  Select,
  Stack,
} from "@chakra-ui/react";
import { range } from "ramda";
import getMonth from "date-fns/getMonth";
import getYear from "date-fns/getYear";

import { TranslationKeys } from "utils/localization";
import AnimatedInput from "../AnimatedInput";
import IconLeftArrow from "../Icons/LeftArrow";
import IconRightArrow from "../Icons/RightArrow";
import classes from "./index.module.css";
import Input from "../Input";

type DatePickerProps = Omit<ChakraInputProps, "type"> & {
  name: string;
  label?: ReactNode;
  error?: FieldError;
  required?: boolean;
  dataTest?: string;
  customError?: TranslationKeys;
  dateValue: Date | null;
  onDateChange: (date: Date | null) => void;
  placeholderText?: string;
  isFutureDate?: boolean;
  isAnimatedInput?: boolean;
};

const getCurrentDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${day}. ${month}. ${year}`;
};

const NewDatePicker: FC<DatePickerProps> = ({
  onDateChange,
  dateValue,
  placeholderText,
  isFutureDate,
  isAnimatedInput = true,
  ...props
}) => {
  const years = isFutureDate
    ? range(getYear(new Date()), getYear(new Date()) + 5)
    : range(1900, getYear(new Date()) + 1);
  const months = [
    "Leden",
    "Únor",
    "Březen",
    "Duben",
    "Květen",
    "Červen",
    "Červenec",
    "Srpen",
    "Září",
    "Říjen",
    "Listopad",
    "Prosinec",
  ];

  return (
    <DatePicker
      popperClassName={classes.DatePicker}
      selected={dateValue}
      placeholderText={isAnimatedInput ? "" : getCurrentDate()}
      dateFormat="dd.MM.yyyy"
      minDate={isFutureDate ? new Date() : null}
      maxDate={isFutureDate ? null : new Date()}
      onChange={onDateChange}
      customInput={
        isAnimatedInput ? (
          <AnimatedInput {...props} placeholderText={placeholderText} />
        ) : (
          <Input {...props} _placeholder={{ color: "ud-grey-300" }} />
        )
      }
      renderCustomHeader={({
        date,
        changeYear,
        changeMonth,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <Stack>
          <Select
            iconSize="0"
            value={getYear(date)}
            onChange={({ target: { value } }) => changeYear(+value)}
            cursor="pointer"
            h={7}
            border="none"
            fontWeight="bold"
            width="35%"
          >
            {years.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
          <Stack direction="row" justifyContent="space-between" margin={3}>
            <Select
              value={months[getMonth(date)]}
              onChange={({ target: { value } }) =>
                changeMonth(months.indexOf(value))
              }
              h={7}
              border="none"
              width="50%"
              appearance="none"
            >
              {months.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>

            <Stack direction="row">
              <IconButton
                aria-label="Previous month"
                icon={
                  <IconLeftArrow fontSize={20} fontWeight="bold" ml="-2px" />
                }
                onClick={decreaseMonth}
                disabled={prevMonthButtonDisabled}
                p={1}
                minW="auto"
                h="max-content"
                backgroundColor="transparent"
                borderRadius="100%"
                _hover={{ backgroundColor: "ud-grey-300" }}
              />
              <IconButton
                aria-label="Next month"
                icon={
                  <IconRightArrow fontSize={20} fontWeight="bold" mr="-2px" />
                }
                onClick={increaseMonth}
                disabled={nextMonthButtonDisabled}
                p={1}
                minW="auto"
                h="max-content"
                backgroundColor="transparent"
                borderRadius="100%"
                _hover={{ backgroundColor: "ud-grey-300" }}
                _disabled={{ backgroundColor: "none", opacity: 0.2 }}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
    />
  );
};

export default NewDatePicker;
